import makeStyles from '@mui/styles/makeStyles';
import classNames from "classnames";
import React from "react";
import {useTranslation} from "react-i18next";
import {activeThemeIndex} from "business/curriculumTheme";
import {color, font, radius, type} from "common/tokens";
import SectionLinkHeader from "./SectionLinkHeader";
import ThemeIcon from "./ThemeIcon";

const CIRCLE = 36;
// Breathing room between a circle and the line running into it.
const CONNECTOR_GAP = 12;

// Titles like "Introduction(Foundation)" carry no space before the bracket, so
// they are one unbreakable word wider than the column and spill into the step
// beside them. A zero-width space offers the break a reader would pick anyway.
const wrappable = title => String(title || '').replace(/\(/g, '\u200B(');

const useStyles = makeStyles({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: 31,
  },
  track: {
    display: 'flex',
    alignItems: 'flex-start',
  },
  step: {
    flex: '1 1 0',
    minWidth: 0,
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
  },
  circle: {
    width: CIRCLE,
    height: CIRCLE,
    borderRadius: radius.PILL,
    border: `2px solid ${color.BORDER}`,
    background: color.SURFACE_MUTED,
    color: color.TEXT_MUTED,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
  },
  circleActive: {
    background: color.SURFACE,
    borderColor: color.ACCENT_TEXT,
    color: color.ACCENT_TEXT,
  },
  circleComplete: {
    background: color.ACCENT_TEXT,
    borderColor: color.ACCENT_TEXT,
    color: color.SURFACE,
    boxShadow: `0 0 0 4px ${color.PURPLE_TINT}`,
  },
  label: {
    ...type.chipSmall,
    fontSize: 12,
    lineHeight: '16px',
    letterSpacing: 'normal',
    textTransform: 'none',
    fontWeight: font.REGULAR,
    color: color.TEXT_MUTED,
    textAlign: 'center',
    margin: 0,
    padding: '0 10px',
    boxSizing: 'border-box',
    width: '100%',
    overflowWrap: 'break-word',
  },
  labelDone: {
    color: color.ACCENT_TEXT,
  },
  // Spans the gap between this step's circle and the previous one, so the
  // track reads as one line however many milestones there are.
  connector: {
    position: 'absolute',
    top: CIRCLE / 2 - 1.5,
    left: `calc(-50% + ${CIRCLE / 2 + CONNECTOR_GAP}px)`,
    width: `calc(100% - ${CIRCLE + CONNECTOR_GAP * 2}px)`,
    height: 3,
    borderRadius: 8,
    background: color.BORDER,
  },
  connectorDone: {
    background: color.ACCENT_TEXT,
  },
  helpText: {
    ...type.chip,
    fontWeight: font.REGULAR,
    lineHeight: '17.4px',
    color: color.TEXT_MUTED,
    margin: 0,
  },
});

const MilestoneStepper = ({themes = [], viewAllTo = '/mentorship/milestones'}) => {
  const classes = useStyles();
  const {t} = useTranslation();

  if (!themes.length) {
    return null;
  }

  const activeIndex = activeThemeIndex(themes);

  return (
    <div className={classes.wrapper}>
      <SectionLinkHeader label={t('curriculumMilestones')} to={viewAllTo}/>

      <div className={classes.track}>
        {themes.map((theme, index) => {
          const isComplete = theme.isComplete;
          const isActive = index === activeIndex && !isComplete;
          const reached = isComplete || isActive;

          return (
            <div className={classes.step} key={theme.id}>
              {index > 0 && (
                <span
                  className={classNames(
                    classes.connector,
                    themes[index - 1].isComplete && classes.connectorDone
                  )}
                />
              )}
              <span
                className={classNames(
                  classes.circle,
                  isActive && classes.circleActive,
                  isComplete && classes.circleComplete
                )}
              >
                <ThemeIcon banner={theme.banner} size={16}/>
              </span>
              <p className={classNames(classes.label, reached && classes.labelDone)}>
                {wrappable(theme.title)}
              </p>
            </div>
          );
        })}
      </div>

      <p className={classes.helpText}>{t('milestonesHelpText')}</p>
    </div>
  );
};

export default MilestoneStepper;
